import AWS from 'aws-sdk';
import auth0 from 'auth0-js';
import router from '../router';
import api from './api';
import sendEvent from './sendEvent';

const auth0WebAuth = new auth0.WebAuth({
  domain: process.env.VUE_APP_AUTH0_DOMAIN,
  clientID: process.env.VUE_APP_AUTH0_CLIENTID,
  redirectUri: `${process.env.VUE_APP_URL}/callback`,
  responseType: 'token id_token',
  scope: 'openid',
});

const getSupplier = async function (userId) {
  const supplier = await api({
    endpoint: `${process.env.VUE_APP_API}/suppliers`,
    path: '/check',
    method: 'POST',
    body: { userId },
  });
  return supplier;
};

const getAwsCredentials = async function (token) {
  AWS.config.update({ region: process.env.VUE_APP_REGION });

  AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: process.env.VUE_APP_IDENTITYPOOLID,
    Logins: {
      [process.env.VUE_APP_AUTH0_DOMAIN]: token,
    },
  });

  return AWS.config.credentials.getPromise();
};

const setSession = async (authResult) => {
  try {
    await getAwsCredentials(authResult.idToken);
  } catch (err) {
    await sendEvent({
      type: 'ERROR-SUPPLIERS-FRONT-AWS-CREDENTIALS',
      itemId: authResult.idToken,
      errorMessage: err,
    });
  }

  localStorage.setItem('loggedIn', true);
  const now = new Date().getTime();
  const expireTime = now + (authResult.expiresIn * 1000) - (5 * 60 * 1000);
  localStorage.setItem('expireTime', expireTime);
  localStorage.setItem('auth0userId', authResult.idTokenPayload.sub);
};

export function logout() {
  localStorage.removeItem('loggedIn');
  localStorage.removeItem('expireTime');
  localStorage.removeItem('supplierId');

  if (AWS.config.credentials) {
    AWS.config.credentials.clearCachedId();
    AWS.config.credentials = new AWS.CognitoIdentityCredentials({});
  }

  auth0WebAuth.logout({
    returnTo: process.env.VUE_APP_URL,
  });
}

const renewSession = async () => new Promise(async (resolve, reject) => {
  auth0WebAuth.checkSession({}, async (err, authResult) => {
    if (authResult && authResult.accessToken && authResult.idToken) {
      await setSession(authResult);
      resolve();
    } else if (err) {
      logout();
      reject();
    }
  });
});

const checkSession = async () => {
  const now = new Date().getTime();
  if (!AWS.config.credentials || now > AWS.config.credentials.expireTime.getTime() || now > localStorage.getItem('expireTime')) {
    await renewSession();
  }
};

const login = (to) => {
  auth0WebAuth.authorize({
    redirectUri: `${process.env.VUE_APP_URL}/callback`,
    mode: to.name === 'NewUser' ? 'signUp' : 'login',
  });
};

export async function requireAuth(to, from, next) {
  if (localStorage.getItem('loggedIn') === 'true') {
    await checkSession();

    if (to.path != '/start' && to.path != '/activate' && to.path != '/welcome') {
      const userId = localStorage.getItem('auth0userId');
      try {
        const supplier = await getSupplier(userId);
        if (supplier.active && (supplier.paid || supplier.primary)) {
          next();
        } else if (supplier.active) {
          if (["/faqs","/blog","/account","/contact/authorised","/contact/general","/contact/technical"].indexOf(to.path) === -1) { // Allow only this routes before pay
            next('/welcome');
          } else {
            next();
          }
        } else {
          next('/start');
        }
      } catch (err) {
        await sendEvent({
          type: 'ERROR-SUPPLIERS-FRONT-GET-SUPPLIER',
          itemId: userId,
          errorMessage: err,
        });
      }
    } else {
      next();
    }
  } else {
    next(false);
    login(to);
  }
}

export function handleAuthentication() {
  auth0WebAuth.parseHash(async (err, authResult) => {
    if (authResult && authResult.accessToken && authResult.idToken) {
      await setSession(authResult);

      try {
        const supplier = await getSupplier(authResult.idTokenPayload.sub);
        localStorage.setItem('supplierId', supplier.supplierId);
        if (supplier.active) {
          router.replace('/');
        } else {
          router.replace('/start');
        }
      } catch (error) {
        await sendEvent({
          type: 'ERROR-SUPPLIERS-FRONT-GET-SUPPLIER',
          itemId: authResult.idTokenPayload.sub,
          itemData: authResult,
          errorMessage: error,
        });
      }
    } else if (err) {
      await sendEvent({
        type: 'ERROR-SUPPLIERS-FRONT-HANDLE-AUTH',
        itemData: authResult,
        errorMessage: err,
      });
      logout();
    }
  });
}

export async function isLoggedIn() {
  if (localStorage.getItem('loggedIn') === 'true') {
    await checkSession();
    return true;
  }
  return false;
}
